import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import { useVirtualizer } from '@tanstack/react-virtual';
import clsx from 'clsx';
import { lazy, useMemo, useRef, useState } from 'react';

import { useModelTextDialects } from '#components/model-text/use-model-text-dialects.js';
import { SignalChip } from '#src/components/signal-chip/signal-chip.js';
import { getModulePinoutRowSearchHaystack } from './get-module-pinout-row-search-haystack.js';

import type { ModulePinoutRow } from './get-module-pinout-rows.js';

const NetChip = lazy(() => import('#components/net-chip/net-chip.js').then((m) => ({ default: m.NetChip })));

/** Shared column styles for the pinout table. */
const SHARED_GRID_STYLES = clsx('grid grid-cols-[3rem_8rem_4rem_3rem_12rem] *:px-1.5');
/** Fallback row height used before a row has been measured; real heights are measured on render. */
const ESTIMATED_ROW_HEIGHT = 24;

export type ModuleCardPinoutTabProps = {
	rows: ModulePinoutRow[];
};

export function ModuleCardPinoutTab({ rows }: ModuleCardPinoutTabProps) {
	const [filter, setFilter] = useState('');
	const scrollRef = useRef<HTMLDivElement>(null);
	const dialects = useModelTextDialects();

	const filteredRows = useMemo(() => {
		const query = filter.trim().toLowerCase();
		if (query === '') {
			return rows;
		}

		return rows.filter((row) =>
			getModulePinoutRowSearchHaystack(row, dialects).some((field) => field.toLowerCase().includes(query)),
		);
	}, [rows, filter, dialects]);

	const virtualizer = useVirtualizer({
		count: filteredRows.length,
		getScrollElement: () => scrollRef.current,
		estimateSize: () => ESTIMATED_ROW_HEIGHT,
		getItemKey: (index) => filteredRows[index]?.pinRef ?? index,
		overscan: 8,
	});

	if (rows.length === 0) {
		return <p className='p-3 text-gray-a8 italic'>No pinout information available</p>;
	}

	const virtualItems = virtualizer.getVirtualItems();

	return (
		<div className='flex min-h-0 grow flex-col gap-2'>
			<TextField
				value={filter}
				onChange={(e) => setFilter(e.target.value)}
				placeholder={`Filter ${rows.length} pins — signal, number, net…`}
				className='m-2 mb-0'
				slotProps={{
					input: {
						startAdornment: (
							<InputAdornment position='start'>
								<SearchIcon />
							</InputAdornment>
						),
					},
				}}
			/>

			<div ref={scrollRef} className='min-h-0 grow overflow-auto'>
				{filteredRows.length === 0 && <p className='p-3 text-gray-a8 italic'>No pins match the filter</p>}

				<div
					className={clsx(
						SHARED_GRID_STYLES,
						'sticky top-0 z-1 w-max bg-gray-1 py-2 text-sm font-bold tracking-wide text-gray-a11 uppercase',
					)}
				>
					<span className='text-right'>#</span>
					<span>Signal</span>
					<span>Type</span>
					<span>Dir.</span>
					<span>Net</span>
				</div>
				<div className='relative w-max' style={{ height: virtualizer.getTotalSize() }}>
					{virtualItems.map((virtualItem) => {
						const row = filteredRows[virtualItem.index];
						if (row === undefined) {
							return null;
						}
						return (
							<div
								key={virtualItem.key}
								data-index={virtualItem.index}
								ref={virtualizer.measureElement}
								className={clsx(SHARED_GRID_STYLES, 'absolute top-0 left-0 w-full items-center py-1')}
								style={{
									transform: `translateY(${virtualItem.start}px)`,
								}}
							>
								<span className='text-right text-gray-a8 tabular-nums'>{row.pinNumber}</span>
								<div>
									{row.signal !== null ?
										<SignalChip signal={row.signal} allSignals={[]} tooltipDisableInteractive />
									:	<div />}
								</div>
								<span>{row.type}</span>
								<span>{row.direction}</span>
								<div>
									{row.net !== null ?
										<NetChip net={row.net} />
									:	<div />}
								</div>
							</div>
						);
					})}
				</div>
			</div>
		</div>
	);
}
